import React from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {toast} from 'react-toastify';

import {ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {
    loadParticipantsDataFx,
    onChangeParticipantIdForRemoveConfirmation,
    participantIdForRemoveConfirmation$,
    ParticipantsGate,
    participantsState$,
    removeParticipantFx,
} from 'models/profile/common';

import {
    boundUserWithParticipantFx,
    idsForRemoveBoundedConfirmation$,
    onChangeIdsForBoundConfirmation,
} from 'models/profile/staff/table';

import {ParticipantItem} from './ParticipantItem';
import {
    CancelConfirmButton,
    ConfirmRemoveFooter,
    SpinnerLayer,
    SubmitConfirmButton,
} from './styled';

export const ParticipantList: React.FC = () => {
    const {t} = useTranslation();
    useGate(ParticipantsGate);
    const state = useStore(participantsState$);
    const participantId = useStore(participantIdForRemoveConfirmation$);
    const ids = useStore(idsForRemoveBoundedConfirmation$);

    const ConfirmUnboundUser = () => {
        const onUnboundUser = async () => {
            if (!ids) return;
            try {
                await boundUserWithParticipantFx(ids);
                onChangeIdsForBoundConfirmation(null);
                await loadParticipantsDataFx();
            } catch (error) {
                toast.error(t('errors.occurred'));
            }
        };
        const translate = (key: string) => t('pages.profile.staff.confirm.' + key);
        return (
            <Modal modalWidth={0} show={!!ids} hideCloseButton onClose={() => {}}>
                <p style={{textAlign: 'center'}}>{translate('message')}</p>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        variant={ButtonVariant.Outline}
                        onClick={() => onChangeIdsForBoundConfirmation(null)}
                    >
                        {translate('cancel')}
                    </CancelConfirmButton>
                    <SubmitConfirmButton onClick={() => onUnboundUser()}>
                        {translate('ok')}
                    </SubmitConfirmButton>
                </ConfirmRemoveFooter>
            </Modal>
        );
    };

    const ConfirmRemoveParticipant = () => {
        const onRemoveParticipant = async () => {
            if (!participantId) return;
            try {
                await removeParticipantFx(participantId);
                onChangeParticipantIdForRemoveConfirmation(null);
            } catch (error) {
                console.error(error);
            }
        };
        const translate = (key: string) => t('pages.profile.participant.confirm.' + key);
        return (
            <Modal modalWidth={0} show={!!participantId} hideCloseButton onClose={() => {}}>
                <p style={{textAlign: 'center'}}>{translate('message')}</p>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        variant={ButtonVariant.Outline}
                        onClick={() => onChangeParticipantIdForRemoveConfirmation(null)}
                    >
                        {translate('cancel')}
                    </CancelConfirmButton>
                    <SubmitConfirmButton onClick={onRemoveParticipant}>
                        {translate('ok')}
                    </SubmitConfirmButton>
                </ConfirmRemoveFooter>
            </Modal>
        );
    };

    return (
        <>
            {state.loading ? (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            ) : (
                <>
                    {state.data.map((participant) => (
                        <ContentContainer key={participant.id}>
                            <ParticipantItem participant={participant} key={participant.id} />
                        </ContentContainer>
                    ))}
                    {participantId && <ConfirmRemoveParticipant />}
                    {ids && <ConfirmUnboundUser />}
                </>
            )}
        </>
    );
};
